import { diffWords } from 'diff';
import { countWords } from './word-count';

export type DocumentVersionStatus = 'ready' | 'invalid' | 'pending_migration';
export type DocumentVersionFormat = 'yjs_update' | 'yjs_snapshot';
export type VersionInspectorMode = 'changes' | 'fullText';

export const VERSION_ID_SEARCH_PARAM = 'versionId';
export const COMPARE_VERSION_ID_SEARCH_PARAM = 'compareVersionId';
export const VERSION_VIEW_SEARCH_PARAM = 'versionView';

export interface DocumentVersionListItem {
  id: string;
  documentId: string;
  createdBy: string | null;
  userEmail?: string;
  label: string | null;
  description: string | null;
  restoredFrom: string | null;
  format?: DocumentVersionFormat;
  status?: DocumentVersionStatus;
  canRestore?: boolean;
  createdAt: string;
}

export type VersionInspectorSearchState = {
  versionId: string | null;
  compareVersionId: string | null;
  mode: VersionInspectorMode | null;
};

export function parseVersionInspectorSearchParams(
  searchParams: URLSearchParams,
): VersionInspectorSearchState {
  const versionIdRaw = searchParams.get(VERSION_ID_SEARCH_PARAM);
  const compareVersionIdRaw = searchParams.get(COMPARE_VERSION_ID_SEARCH_PARAM);
  const viewRaw = searchParams.get(VERSION_VIEW_SEARCH_PARAM);
  const versionId = versionIdRaw && versionIdRaw.length > 0 ? versionIdRaw : null;
  const compareVersionId =
    compareVersionIdRaw && compareVersionIdRaw.length > 0 ? compareVersionIdRaw : null;
  const mode: VersionInspectorMode | null =
    viewRaw === 'changes' || viewRaw === 'fullText'
      ? viewRaw
      : versionId
        ? 'changes'
        : null;
  return {
    versionId,
    compareVersionId,
    mode: versionId ? mode : null,
  };
}

export function applyVersionInspectorSearchParams(
  searchParams: URLSearchParams,
  state: VersionInspectorSearchState | null,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(VERSION_ID_SEARCH_PARAM);
  next.delete(COMPARE_VERSION_ID_SEARCH_PARAM);
  next.delete(VERSION_VIEW_SEARCH_PARAM);
  if (!state?.versionId) {
    return next;
  }
  next.set(VERSION_ID_SEARCH_PARAM, state.versionId);
  const mode = state.mode ?? 'changes';
  next.set(VERSION_VIEW_SEARCH_PARAM, mode);
  if (mode === 'changes' && state.compareVersionId) {
    next.set(COMPARE_VERSION_ID_SEARCH_PARAM, state.compareVersionId);
  }
  return next;
}

export function versionInspectorSearchStatesEqual(
  left: VersionInspectorSearchState,
  right: VersionInspectorSearchState,
): boolean {
  return left.versionId === right.versionId
    && left.compareVersionId === right.compareVersionId
    && left.mode === right.mode;
}

export function buildVersionComparisonUrl(
  currentUrl: string,
  state: {
    versionId: string;
    compareVersionId: string;
  },
): string {
  const url = new URL(currentUrl);
  url.search = applyVersionInspectorSearchParams(url.searchParams, {
    versionId: state.versionId,
    compareVersionId: state.compareVersionId,
    mode: 'changes',
  }).toString();
  return url.toString();
}

export function isVersionInspectable(version: { status?: DocumentVersionStatus }): boolean {
  return version.status !== 'invalid';
}

export function isDailyVersionLabel(label: string | null | undefined): boolean {
  if (!label) {
    return false;
  }
  return /^Daily \d{4}-\d{2}-\d{2}$/.test(label.trim());
}

export function isNamedVersionLabel(label: string | null | undefined): boolean {
  return Boolean(label && label.trim().length > 0);
}

export function isManualVersionLabel(label: string | null | undefined): boolean {
  return isNamedVersionLabel(label) && !isDailyVersionLabel(label);
}

export function filterDocumentVersionsForDisplay(
  versions: DocumentVersionListItem[],
  options: { namedOnly?: boolean; manualOnly?: boolean },
): DocumentVersionListItem[] {
  return versions.filter((version) => {
    if (options.manualOnly && !isManualVersionLabel(version.label)) {
      return false;
    }
    if (options.namedOnly && !isNamedVersionLabel(version.label)) {
      return false;
    }
    return true;
  });
}

export function findOlderReadyVersionId(
  versions: Array<{ id: string; status?: DocumentVersionStatus }>,
  currentId: string,
): string | null {
  const start = versions.findIndex((version) => version.id === currentId);
  if (start < 0) {
    return null;
  }

  for (let index = start + 1; index < versions.length; index += 1) {
    if (isVersionInspectable(versions[index])) {
      return versions[index].id;
    }
  }

  return null;
}

export function findNewerReadyVersionId(
  versions: Array<{ id: string; status?: DocumentVersionStatus }>,
  currentId: string,
): string | null {
  const start = versions.findIndex((version) => version.id === currentId);
  if (start < 0) {
    return null;
  }

  for (let index = start - 1; index >= 0; index -= 1) {
    if (isVersionInspectable(versions[index])) {
      return versions[index].id;
    }
  }

  return null;
}

export function summarizeWordDiff(before: string, after: string): { added: number; removed: number } {
  const parts = diffWords(before, after);
  let added = 0;
  let removed = 0;

  for (const part of parts) {
    if (part.added) {
      added += countWords(part.value);
    }
    if (part.removed) {
      removed += countWords(part.value);
    }
  }

  return { added, removed };
}
